/**
 * THE CAST ON THE PITCH — a render subsystem that draws 22 DIFFERENT people.
 *
 * ── Why this is a sibling of `playerVisual.ts` and not an edit to it ───────
 *
 * `playerVisual.ts` is built around one sentence: *one geometry, one material,
 * one atlas, twenty-two skeletons*. That sharing is not an optimisation bolted
 * onto it, it IS its architecture — `buildSlot` clones a template and hands
 * every slot the same `sharedAsset`, and the team strip is a ROW INDEX into one
 * palette texture rather than a second material. A cast inverts every one of
 * those decisions. Editing that file to do both would leave the shipped path
 * carrying branches for a path it never takes, in the one subsystem that must
 * not regress.
 *
 * So this is an ADDITION. `subsystems.ts` picks one or the other, today's path
 * is untouched and still the default, and turning the cast off is one flag.
 *
 * ── What is shared and what is not ─────────────────────────────────────────
 *
 * SHARED with the existing path, on purpose, because animation behaviour must
 * not fork: `PlayerAnimator`, `AnimationDirector`, `AnimCommand`, the mixer
 * throttle bands, the blob-shadow instancing, the shadow-caster promotion.
 * A cast player and a master player move identically; they only look different.
 *
 * NOT shared: the asset. Every character brings his own geometry, his own
 * materials and his own textures, and the clip pack is the twelve authored
 * `M_*.glb` on the 74-bone rig — the same rig every cast member ships, which is
 * the entire reason one pack drives thirty-six people.
 *
 * ── The honest cost, AFTER the merge+atlas pass ───────────────────────────
 *
 * This paragraph used to describe the pre-optimisation cast — 11-13 meshes over
 * 17-19 materials each, ~400 draw calls and ~550 MiB for twenty-two of them —
 * and that is no longer what loads. The cast now points at `cast-opt/`: Draco
 * geometry, a shared `_shared/` KTX2 pool cached ACROSS files by
 * `castDecoders.ts`, and a merged single-primitive atlas at LOD2/LOD3.
 *
 * Measured at the broadcast camera by `scripts/bench-cast.mjs`: 36 DISTINCT
 * characters for 42 draw calls — 36 characters plus 6 scene objects, i.e. one
 * draw call each — 1.19-3.60 ms animated, and 19 MB of GPU texture against
 * 1,130 MB for the unoptimised cast. On disk 52.1 MB against 462.6 MB.
 *
 * The merge is deliberately FAR-LOD ONLY (see CAST_DEFAULT_ON below), so LOD0
 * and LOD1 still carry 17-20 primitives and a near camera costs more than the
 * headline. This subsystem does not hide that either: `perf()` reports draw
 * calls, triangles, resident texture bytes and the LOD histogram per frame.
 */
import {
  CircleGeometry,
  Color,
  DoubleSide,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Quaternion,
  Vector3,
  type AnimationClip,
  type Object3D,
  type SkinnedMesh,
} from 'three';
import type { QualitySettings, RenderContext, RenderState, RenderSubsystem } from '../context';
import {
  handAnchorFromRig,
  hasHands,
  type BallHoldPose,
  type HandAnchorProvider,
  type SilhouetteProvider,
} from '../handAttach';
import { AnimationDirector } from './animationDirector';
import { PlayerAnimator } from './playerAnimator';
import { bandForDistanceSq, mixerHzForBand } from './animBands';
import type { AnimCommand } from './animationCommand';
import { AUTHORED_CLIP_STEMS, loadAuthoredClipPack } from './authoredMasterLoader';
import type { CastLodLevel } from './castContract';
import { CAST_CHARACTERS, CAST_MANIFEST_VERSION, CAST_TOTALS } from './castManifest';
import {
  assignCast,
  castCoachFor,
  castIdsForMatch,
  castOfficial,
  type CastAssignment,
  type CastSlot,
} from './castRegistry';
import {
  castLodLoaded,
  castRefusals,
  instantiateCast,
  preloadCast,
  type CastInstance,
} from './castLoader';
import { CAST_PRELOAD_LOD, castLevelsForQuality, castLodStep } from './castLod';
import {
  coachPlacement, refereePlacement, assistantPlacement, benchPlacement,
} from './castOfficials';
import type { CastDebugInfo } from './castFallbackBanner';
import { applyHeadLookAt, createHeadLookState, type HeadLookState } from './headLookAt';

const SLOTS = 32;

// Mixer update rate by distance band — shared with `playerVisual.ts` so the two
// cannot drift. The NEAR band animates at the full render rate; see
// animBands.ts for the vertical pop that came of throttling it to 30 Hz.
const SHADOW_CASTERS: Record<string, number> = { low: 0, medium: 0, high: 4, ultra: 6 };

/**
 * How many LOD swaps may happen in one frame.
 *
 * A swap allocates a skeleton and a mixer for a tier this slot has not drawn
 * before. Twenty-two of them on the frame the camera cuts is a visible hitch, so
 * they are rationed; a slot that is denied keeps the tier it has for one more
 * frame, which is invisible.
 */
const SWAPS_PER_FRAME = 2;

/**
 * Metres added to every character's cull sphere to cover skinned pose drift.
 * Measured worst case on the shipped cast was 0.404 m of centre drift plus
 * 0.090 m of radius growth; see `inflateForPose`.
 */
const POSE_MARGIN_M = 0.75;

/**
 * The speed an official is never slower than, metres per second.
 *
 * A referee covers 10-12 km in a match and is jogging for most of it: he is
 * moving toward the NEXT phase of play even when the ball is not moving. Those
 * are exactly the moments his placement barely changes, so a gait chosen purely
 * by differentiating that placement would idle him — the statue this replaced,
 * one step removed.
 *
 * 2.3 sits inside the Jog band ([1.40, 2.478) in playerAnimator.ts) with room on
 * both sides: it cannot fall through to Walk, and an official who is genuinely
 * sprinting after play still steps up to Run.
 *
 * ⚠ THE JOG FLOOR IS GONE (owner, 2026-08-14: "he is ... always running fix
 * that"). OFFICIAL_JOG_FLOOR_MS (2.3) kept the referee jogging even when his
 * placement had not moved a centimetre — jogging on the spot beside a dead
 * ball, which read as a man copying the players instead of officiating. What
 * replaces it is honesty with a filter: the differentiated speed is smoothed
 * (STAFF_SPEED_TAU_S) so placement jitter cannot flicker him between Idle and
 * Walk, and the ladder gets the smoothed truth. When his spot moves he walks,
 * jogs or runs by exactly how fast it moves; when it stops, HE STOPS.
 */
const STAFF_SPEED_TAU_S = 0.25;

/**
 * How fast an official may run to his spot, metres per second.
 *
 * `refereePlacement` is a pure function of the ball, so its output can jump the
 * length of a pass in one frame. Integrating toward it at a capped speed is what
 * turns that target into a man: a long ball now sends him off at a run and he
 * arrives a second or two later, instead of arriving instantly and reading as a
 * cursor welded to the ball.
 *
 * 7.0 is a fit official's sprint — above the squad's Run divisor (3.1) and below
 * Sprint's 5.794 x the 1.45 rate clamp, so the gait ladder can draw everything
 * he does without the clamp lying about it.
 */
const OFFICIAL_MAX_SPEED_MS = 7.0;

/**
 * Below this distance from his spot he closes proportionally rather than at the
 * cap, so a 2 m correction is a walk and not a sprint-and-stop.
 */
const OFFICIAL_EASE_M = 6.0;

/**
 * Above this speed a staff member's BODY follows his path rather than his gaze.
 *
 * An official past it follows his path and drops back to the supplied yaw
 * only when he has genuinely stopped — at which point there is no travel
 * direction to face. A
 * coach spends most of the match below it, which is correct: he is standing,
 * turned toward the pitch, not toward the last centimetre he shuffled.
 */
const FACING_FROM_TRAVEL_MS = 0.35;

/** Is the cast path on? URL wins, then the default. */
export function castEnabledByDefault(): boolean {
  if (typeof location !== 'undefined') {
    const q = new URLSearchParams(location.search).get('cast');
    if (q === 'on' || q === '1') return true;
    if (q === 'off' || q === '0') return false;
  }
  return CAST_DEFAULT_ON;
}

/**
 * DEFAULT ON. The condition the previous note set has been met, and it was met
 * by measurement rather than by assertion.
 *
 * That note said: flip this the day the merge+atlas pass has been run for the
 * cast, when `castManifest.ts` shows 1 mesh / 1 material / 1 texture per
 * character. `atlas_merge.mjs` did it: LOD2 and LOD3 are `meshes: 1,
 * primitives: 1, materials: 1` for all 36, taking the 36-character broadcast
 * frame from 484 draw calls to 42 — one per character plus six scene objects —
 * and submit time from 12.56-29.63 ms to 0.60-0.95 ms. Texture is a shared
 * sha256-keyed KTX2 pool: 19 MB for the whole cast, not ~25 MiB each.
 *
 * ⚠ THE 42-DRAW FIGURE IS A BROADCAST-CAMERA NUMBER. The atlas pass is FAR-LOD
 * ONLY, by design. LOD0/LOD1 are deliberately NOT merged and still carry 17-20
 * primitives each, so the jersey and skin keep their per-material KHR sheen up
 * close — and because atlasing makes texels per-character, which is the
 * opposite of the shared `_shared/` pool that took the cast from 462.6 MB to
 * 52.1 MB. Merging the near tiers would cost more than it saves.
 *
 * At the broadcast camera 34 of 36 characters sit at LOD2/LOD3, which is where
 * the win is spent. The gameplay camera at 17-20 m sits on the LOD1/LOD2 edge
 * (`castLod.ts` CAST_LOD_BANDS), so a tight camera can still pull several
 * players onto the unmerged LOD1 tier — a known, bounded cost, held by
 * `CAST_LEVELS_FOR_TIER` keeping LOD0 off anything below `ultra` and LOD1 off
 * anything below `high`. Do not quote 42 for a close shot.
 *
 * Turning this OFF silently reverts the whole game to the single shared master,
 * which is why it was worth a comment rather than a bare boolean: every QA
 * render taken off a branch that still had this `false` photographed the
 * placeholder character while its author believed they were looking at the cast.
 * `?cast=off` still forces the old path for a one-off comparison;
 * `castEnabledByDefault()` reads it before this constant.
 */
export const CAST_DEFAULT_ON = true;

interface SlotState {
  readonly castId: string;
  /** One instance per tier this slot has drawn. Reused, never rebuilt. */
  readonly tiers: Map<CastLodLevel, { inst: CastInstance; animator: PlayerAnimator; director: AnimationDirector }>;
  lod: CastLodLevel;
  active: { inst: CastInstance; animator: PlayerAnimator; director: AnimationDirector } | null;
  accum: number;
  band: 0 | 1 | 2;
  visible: boolean;
  /** Eased head-turn state, per SLOT so it survives an LOD swap. */
  readonly headLook: HeadLookState;
  /** The active tier's head bone, looked up once per tier and cached. */
  headBone: Object3D | null;
  headBoneTier: number;
  casting: boolean;
  /**
   * The frame id three last SUBMITTED one of this slot's meshes on.
   *
   * Written by an `onAfterRender` witness, never by this subsystem's own
   * bookkeeping — see `tierFor`. It is the difference between "we meant to draw
   * him" and "he is on screen".
   */
  drawnFrame: number;
}

export interface CastPerfSample {
  players: number;
  drawCalls: number;
  triangles: number;
  textureBytesResident: number;
  lodHistogram: [number, number, number, number];
  swapsThisFrame: number;
  animMs: number;
  /**
   * HOW MANY PLAYERS THE RENDERER ACTUALLY DREW LAST FRAME.
   *
   * `players` above is a SLOT count: it increments for every live slot that
   * owns an active tier, and it cannot see a body three culled, a root left
   * hidden by a bad swap, or a material that never compiled. It read a healthy
   * 22 on a broadcast frame that contained six people. `drawCalls` is no better
   * — it sums a constant off the instance.
   *
   * This one is counted from `onAfterRender`, which three calls once per object
   * that survived culling and reached a render list. If it disagrees with
   * `players`, `players` is the number that is lying. Lags by exactly one frame,
   * because the count it reports is the one the previous render left behind.
   */
  submitted: number;
}

export interface CastVisualHandle {
  readonly ready: boolean;
  readonly debug: () => CastDebugInfo;
  perf(): CastPerfSample;
  /** The resolved team sheet, for a QA harness that wants to name who is where. */
  readonly assignment: CastAssignment | null;
  /** Force a tier on every slot. Benchmark only; -1 restores distance selection. */
  pinLod(level: CastLodLevel | -1): void;
  /**
   * Ask one drawn slot to play a SEMANTIC action, for a verification harness
   * that has no simulation behind it.
   *
   * The master path has carried this on `PlayerVisualHandle` since the wiring
   * landed; the cast path did not, which meant the only way to prove a command
   * draws its clip was to reproduce the gameplay situation that produces it —
   * for SLIDE that is a tackle inside a live match, and for JUMP an aerial
   * duel. Both are reachable and neither is reliably reproducible in a capture
   * script, so "does JUMP draw anything?" stayed unanswerable from outside.
   *
   * Production gameplay does NOT call this: the director derives every action
   * from `RenderState.anim`, which the simulation owns. It takes an
   * `AnimCommand` and never a clip name, so it cannot be used to smuggle the
   * clip vocabulary back out of the animator.
   *
   * @returns true only if the action actually started — false for a slot that
   *   is not drawn, and false for a command with no clip bound, which is the
   *   answer a verification run wants rather than a silent success.
   */
  requestAction(slot: number, cmd: AnimCommand): boolean;
  /** Clip names the animator asked for and the loaded pack did not contain. */
  missingClips(): readonly string[];
  /**
   * Where slot `n` is standing right now, in world space, or null if it is not
   * drawn.
   *
   * For a capture harness that has to AIM at the player it just drove. Without
   * it the only way to frame a subject is to guess the simulation handle's
   * shape from outside, and a wrong guess does not fail loudly — it silently
   * falls back to a fixed camera pose and photographs empty grass, which is
   * exactly the "proof" that proves nothing. Learned by doing it.
   */
  slotPosition(slot: number): { x: number; y: number; z: number } | null;
  /**
   * Drive a SUSTAINED, non-gait pose directly — the `setSustainedPose` half of
   * the wire `requestAction` cannot reach, because `PlayerAnimator.requestAction`
   * refuses anything that is not a one-shot (`NotAnAction`). A verification
   * harness proving JOCKEY, FATIGUED, FALL, STUMBLE, THROW_IN_HOLD or GK_READY
   * actually moves bones has no simulation to hold the state for it, so this
   * calls the exact same `setLocomotionCommand` path the director calls every
   * frame, at zero velocity.
   *
   * @returns true only if the slot is drawn AND the pose actually took the base
   *   layer — false for an undrawn slot or a command with no clip bound.
   */
  forcePose(slot: number, cmd: AnimCommand): boolean;
  /**
   * One player's live bone LOCAL rotations, keyed by bone name, or null if the
   * slot is not drawn.
   *
   * For a harness that has to prove a clip MOVES something rather than trusting
   * that a clip named plausibly and a mixer weight reaching 1 means the rig
   * deformed — `availabilityOf() === Available` proves a clip is bound, not that
   * driving it changes a single bone. Reads `Bone.quaternion` directly off the
   * instantiated skeleton (post-mixer, pre-render), so two calls a frame apart
   * around a `requestAction`/`forcePose` measure exactly what the render loop
   * would have drawn.
   */
  boneSnapshot(slot: number): Record<string, readonly [number, number, number, number]> | null;
  /**
   * One bone's live WORLD position (metres, scene space), or null if the slot
   * is not drawn or the name does not match a bone.
   *
   * `boneSnapshot` proves a clip moves a bone at all; this answers "moves it
   * to WHERE" — e.g. verifying a held prop (the throw-in ball) sits at the
   * hand's actual height rather than trusting a clip's documented pose. Reads
   * `Bone.getWorldPosition` post-mixer, pre-render, same seam as
   * `boneSnapshot`.
   */
  boneWorldPosition(slot: number, boneName: string): { x: number; y: number; z: number } | null;
}

export function createCastPlayerVisual(
  opts: { enabled?: boolean } = {},
): RenderSubsystem & { handle: CastVisualHandle } {
  const enabled = opts.enabled ?? castEnabledByDefault();
  const slots = new Array<SlotState | null>(SLOTS).fill(null);
  let assignment: CastAssignment | null = null;
  let bySlot = new Map<number, CastSlot>();
  let clips = new Map<string, AnimationClip>();
  let levels: readonly CastLodLevel[] = [CAST_PRELOAD_LOD];
  let ready = false;
  let fatal: string | null = null;
  let sceneRef: RenderContext['scene'] | null = null;
  /** Held only so `dispose` can retract the hand provider it published at init. */
  let disposeCtx: RenderContext | null = null;
  let shadowCasters = 0;
  let pinned: CastLodLevel | -1 = -1;
  let squadSize = 11;
  /**
   * The officials and coaches, each with the animator the pitch players get.
   *
   * ⚠ THEY WERE STATUES AND THAT WAS WORSE THAN IT SOUNDS. The old comment on
   * `placeStaff` argued that "an official sliding around in bind pose would be
   * worse than one standing still" — which is right, and which is exactly what
   * was shipping: `refereePlacement` moves the referee with the ball on every
   * frame, so he was never standing still. He was gliding across the pitch in
   * bind pose for the whole match.
   *
   * `last` is his previous world position, which is the only speed signal
   * available here: staff are not in `RenderState` (there is no referee in the
   * simulation, see castOfficials.ts) so there is no velocity to read. The
   * position they are PLACED at is differentiated instead.
   */
  interface StaffActor {
    readonly inst: CastInstance;
    readonly animator: PlayerAnimator;
    readonly director: AnimationDirector;
    last: { x: number; z: number } | null;
    /** Exponentially smoothed placement speed — see STAFF_SPEED_TAU_S. */
    smoothSpeed: number;
  }
  const staff = new Map<string, StaffActor>();

  const perf: CastPerfSample = {
    players: 0, drawCalls: 0, triangles: 0, textureBytesResident: 0,
    lodHistogram: [0, 0, 0, 0], swapsThisFrame: 0, animMs: 0, submitted: 0,
  };

  /**
   * Monotonic frame id, stamped onto a slot by the render witness.
   *
   * Bumped at the top of `update()`. A render happens AFTER every update, so the
   * marks present when update N runs are the ones render N-1 left — which is why
   * `perf.submitted` describes the previous frame and says so.
   */
  let frameId = 0;

  /**
   * Seconds since the subsystem started, advanced only by `placeStaff`.
   *
   * The coaches' pacing is a function of TIME, not of anything in RenderState —
   * there is no touchline in the simulation — so it needs a clock, and this is
   * the only place in the file that owns one.
   *
   * ⚠ DECLARED HERE, ABOVE `update()`, NOT beside `placeStaff` down in the
   * helpers. `let` is hoisted but NOT initialised, so a declaration below the
   * call site throws "Cannot access 'staffClock' before initialization" the
   * first time the subsystem updates — which is what it did: the whole render
   * loop died on frame one, in the browser, measured 2026-08-15. The comment
   * that used to sit here said it must be above the return; that was the right
   * instinct and the wrong line.
   */
  let staffClock = 0;

  const blobGeo = new CircleGeometry(0.32, 10);
  blobGeo.rotateX(-Math.PI / 2);
  const blobMat = new MeshLambertMaterial({
    color: new Color(0, 0, 0), transparent: true, opacity: 0.25,
    depthWrite: false, side: DoubleSide,
  });
  const blobs = new InstancedMesh(blobGeo, blobMat, SLOTS);
  blobs.frustumCulled = false;
  blobs.name = 'cast-blob-shadows';

  const mtx = new Matrix4();
  const pos = new Vector3();
const headWorld = new Vector3();
  const scl = new Vector3(1, 1, 1);
  const blobHidden = new Vector3(0, 0, 0);
  const idQ = new Quaternion();
  const up = new Vector3(0, 1, 0);
  const yawQ = new Quaternion();
  const camPos = new Vector3();
  const camDist2 = new Float32Array(SLOTS);
  const liveSlots: number[] = [];
  let pitch = { length: 105, width: 68 };
  const handA = new Vector3();
  const handB = new Vector3();

  /**
   * THE HANDS, for whatever wants to put a prop in them. (§4.5, handAttach.ts)
   *
   * Reads the ACTIVE tier only. A slot that is not drawn, or whose current LOD
   * has no recognisable hands, answers `false` and the ball is left exactly
   * where the sim put it — a far-LOD keeper whose gloves are a merged blob is
   * a worse place to hang a ball than the sim's own socket, and this is drawing
   * that distinction rather than trusting every tier equally.
   *
   * Safe to call after this subsystem's `update`: `subsystems.ts` orders players
   * before the ball, so the mixer has already posed the skeleton and the root
   * has already been moved to this frame's position. `handAnchorFromRig` walks
   * the parent chain itself, because nothing flushes world matrices between the
   * two subsystems.
   */
  const handProvider: HandAnchorProvider = {
    handAnchor(slot, pose, near, out) {
      if (slot < 0 || slot >= SLOTS) return false;
      const s = slots[slot];
      const act = s?.active;
      if (!act || !s.visible) return false;
      return handAnchorFromRig(act.inst.hands, pose as BallHoldPose, near, out, handA, handB);
    },
  };

  /**
   * THE DRAWN SILHOUETTE, for an effect that has to hug an animated body.
   * (handAttach.ts's `SilhouetteProvider`; the consumer is render/boostAura.ts.)
   *
   * Returns the ACTIVE tier's own `meshes` array — never a copy, never a fresh
   * array. That is the whole contract: the array object is stable while this
   * slot draws this tier and becomes a DIFFERENT object the moment a distance
   * swap moves him, so a consumer holding clones of it can see, by identity
   * alone, that what it bound to is no longer what is on screen. It is the same
   * trap this file's header describes for anything attached to a tier, handled
   * by making the swap VISIBLE across the seam rather than by pretending the
   * geometry is per-slot when it is not.
   *
   * Gated on `s.visible` exactly like `handAnchor` above: a slot that is not
   * being drawn has a posed skeleton nobody is looking at, and hanging an aura
   * on it would light up an invisible man.
   */
  const silhouetteProvider: SilhouetteProvider = {
    silhouette(slot) {
      if (slot < 0 || slot >= SLOTS) return null;
      const s = slots[slot];
      const act = s?.active;
      if (!act || !s.visible) return null;
      return act.inst.meshes;
    },
  };

  /**
   * The clip pack, renamed from file stems to the animator's vocabulary.
   *
   * `PlayerAnimator` speaks `Idle/Walk/Jog/Run/Sprint/Backpedal/Strafe*` and
   * `Pass/Penalty/ThrowIn/Header`; the pack ships those twelve names with an
   * `M_` prefix because all twelve are internally called "Animation" and the
   * FILE STEM is the only stable key. Stripping the prefix is the whole mapping
   * — deliberately not a lookup table, because a table would let the two lists
   * drift and a missing clip would then look like a missing animation instead of
   * a missing file.
   */
  function toAnimatorNames(pack: ReadonlyMap<string, AnimationClip>): Map<string, AnimationClip> {
    const out = new Map<string, AnimationClip>();
    for (const [stem, clip] of pack) {
      const name = stem.startsWith('M_') ? stem.slice(2) : stem;
      const copy = clip.clone();
      copy.name = name;
      out.set(name, copy);
    }
    return out;
  }

  /**
   * Widen a skinned mesh's cull sphere to cover every pose its clips can reach.
   *
   * three culls a SkinnedMesh against `geometry.boundingSphere`, which describes
   * the BIND pose and is never recomputed as the skeleton moves. Skinning then
   * carries the vertices outside it. Measured on the shipped cast across the
   * clip library: of 116 skinned meshes, 108 held a sphere that no longer
   * contained their own posed geometry — worst centre drift 0.404 m, worst
   * radius growth 0.090 m. A dive, a slide or a full stride is enough.
   *
   * The visible symptom is a player who is plainly inside the frustum and is
   * simply not drawn, which reads as a mystery culling bug because every
   * position the game knows about is correct — the renderer is testing a stale
   * volume, not a wrong position.
   *
   * A drifted centre is covered by radius alone: growing by drift + growth
   * re-contains the geometry wherever the pose pushed it. POSE_MARGIN_M is that
   * 0.494 m rounded up to 0.75 for headroom on clips not in the measured set.
   * Keeping culling ON and paying a slightly loose sphere is deliberate — the
   * alternative, `frustumCulled = false`, submits every off-camera character
   * every frame, and at a broadcast wide only about half the squad is in shot.
   *
   * ⚠ IT MUST BE THE MESH'S SPHERE, NOT THE GEOMETRY'S. `Frustum.intersectsObject`
   * checks `object.boundingSphere !== undefined` FIRST and only falls back to
   * `geometry.boundingSphere`; SkinnedMesh declares its own, so for these
   * characters the geometry sphere is never consulted. Inflating the geometry
   * instead is a silent no-op — it typechecks, it runs, and the measured
   * before/after is identical to the digit. Confirmed the expensive way.
   *
   * The mesh sphere is per-instance, so the idempotence guard is per-mesh. It
   * cannot live on the geometry: instances of the same character and tier share
   * geometry, and a geometry-keyed guard would inflate the first instance and
   * skip every sibling.
   */
  function inflateForPose(m: SkinnedMesh): void {
    const marked = m.userData as { castPoseMargin?: boolean };
    if (marked.castPoseMargin) return;
    if (!m.boundingSphere) m.computeBoundingSphere();
    const sphere = m.boundingSphere;
    if (!sphere) return;
    marked.castPoseMargin = true;
    sphere.radius += POSE_MARGIN_M;
  }

  function makeTier(castId: string, lod: CastLodLevel):
    { inst: CastInstance; animator: PlayerAnimator; director: AnimationDirector } | null {
    const inst = instantiateCast(castId, lod);
    if (!inst) return null;
    // Loud, once per tier, because a held ball silently falling back to the
    // sim's upright-frame socket is precisely the defect this closes and it
    // would otherwise look like "the fix didn't work".
    if (!hasHands(inst.hands)) {
      console.warn(
        `[cast] ${castId} LOD${lod} exposes no hand bones (hand_l/hand_r) — ` +
          'a ball held by this player will be drawn at the sim socket, not in his gloves.',
      );
    }
    for (const m of inst.meshes) {
      m.castShadow = false;
      m.receiveShadow = true;
      m.frustumCulled = true;
      inflateForPose(m);
    }
    inst.root.visible = false;
    sceneRef?.add(inst.root);
    const animator = new PlayerAnimator({
      clips, root: inst.root, restHipsY: inst.restHipsY,
    });
    return { inst, animator, director: new AnimationDirector(animator) };
  }

  function tierFor(s: SlotState, lod: CastLodLevel) {
    const have = s.tiers.get(lod);
    if (have) return have;
    const made = makeTier(s.castId, lod);
    if (made) {
      // THE DRAW WITNESS. three calls `onAfterRender` exactly once per object
      // that survived frustum culling and reached a render list, so this fires
      // if and only if the renderer submitted this body. It is the only count in
      // this file that is not the subsystem marking its own homework — every
      // other number here says what we INTENDED to draw, and the whole reason
      // this witness exists is that those numbers read 22/22 healthy on a frame
      // with six people in it. Shadow passes do not call it (WebGLShadowMap goes
      // straight to `renderBufferDirect`), so it counts the beauty pass only.
      for (const m of made.inst.meshes) {
        m.onAfterRender = () => {
          s.drawnFrame = frameId;
        };
      }
      s.tiers.set(lod, made);
    }
    return made;
  }

  const handle: CastVisualHandle = {
    get ready() {
      return ready;
    },
    get assignment() {
      return assignment;
    },
    debug: (): CastDebugInfo => ({
      enabled,
      ready,
      filled: assignment ? assignment.onPitch.filter((s) => s.castId).length : 0,
      substituted: assignment
        ? assignment.onPitch
            .filter((s) => !s.castId || castRefusals().has(s.castId))
            .map((s) => ({
              slot: s.number || s.playerId,
              name: s.name,
              why: s.reason ?? castRefusals().get(s.castId!) ?? 'unknown',
            }))
        : [],
      fatal,
    }),
    perf: () => ({ ...perf, lodHistogram: [...perf.lodHistogram] as [number, number, number, number] }),
    pinLod(level) {
      pinned = level;
    },
    requestAction(slot, cmd) {
      const s = slots[slot];
      if (!s?.active) return false;
      return s.active.animator.requestAction(cmd, { interrupt: true });
    },
    slotPosition(slot) {
      const s = slots[slot];
      if (!s?.active || !s.visible) return null;
      const p = s.active.inst.root.position;
      return { x: p.x, y: p.y, z: p.z };
    },
    missingClips() {
      const out = new Set<string>();
      for (const s of slots) {
        if (!s) continue;
        for (const t of s.tiers.values()) {
          for (const m of t.animator.missingClips) out.add(m);
        }
      }
      return [...out].sort();
    },
    forcePose(slot, cmd) {
      const s = slots[slot];
      if (!s?.active) return false;
      const before = s.active.animator.poseClip;
      s.active.animator.setLocomotionCommand(cmd, 0, false, 0);
      // Distinguishes "took the base layer" from "fell through to the gait
      // ladder because nothing is bound" — the latter leaves `poseClip` null.
      return s.active.animator.poseClip !== null && s.active.animator.poseClip !== before
        ? true
        : s.active.animator.poseClip !== null;
    },
    boneSnapshot(slot) {
      const s = slots[slot];
      if (!s?.active) return null;
      const out: Record<string, [number, number, number, number]> = {};
      for (const b of s.active.inst.bones) {
        out[b.name] = [b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w];
      }
      return out;
    },
    boneWorldPosition(slot, boneName) {
      const s = slots[slot];
      if (!s?.active) return null;
      const bone = s.active.inst.bones.find((b) => b.name === boneName);
      if (!bone) return null;
      const v = new Vector3();
      bone.getWorldPosition(v);
      return { x: v.x, y: v.y, z: v.z };
    },
  };

  return {
    name: 'cast-players',
    handle,

    init(ctx: RenderContext): void {
      if (!enabled) {
        ready = true;
        return;
      }
      sceneRef = ctx.scene;
      shadowCasters = SHADOW_CASTERS[ctx.quality.tier] ?? 0;
      disposeCtx = ctx;
      ctx.attachments.setHandProvider(handProvider);
      ctx.attachments.setSilhouetteProvider(silhouetteProvider);
      ctx.scene.add(blobs);
      levels = castLevelsForQuality(ctx.quality.tier);

      if (CAST_TOTALS.charactersBuilt === 0) {
        fatal =
          'the cast manifest is empty — no models under ' +
          'packages/client/public/characters/cast-opt/. Build them with ' +
          '`python assets/player-master/pipeline/build_cast36.py` and freeze with ' +
          '`node scripts/freeze-cast-manifest.mjs`.';
        ready = true;
        console.error('[cast]', fatal);
        return;
      }

      assignment = assignCast({ squadSize });
      bySlot = new Map(assignment.onPitch.map((s) => [s.playerId, s]));

      // A DRIVEABLE SURFACE FOR THE QA HARNESS, DEV ONLY.
      //
      // `handle` already carries everything a verification run needs — who was
      // assigned to which slot, which characters were REFUSED and why, the LOD
      // histogram, the resident texture total. None of it was reachable from
      // outside the module, so "the game loads 36 distinct characters" could
      // only ever be asserted from unit tests against the manifest — which is
      // the tautology castManifestShips.test.ts exists to complain about.
      //
      // Publishing the handle means a browser can read the CAST THE RUNNING
      // GAME ACTUALLY BUILT, after the loads resolved, rather than the cast the
      // manifest promises.
      if (import.meta.env?.DEV && typeof window !== 'undefined') {
        (window as unknown as { __fgCast?: CastVisualHandle }).__fgCast = handle;
      }

      void (async () => {
        try {
          const pack = await loadAuthoredClipPack();
          if (pack.problems.length) {
            console.warn(`[cast] clip pack problems: ${pack.problems.join(' | ')}`);
          }
          clips = toAnimatorNames(pack.clips);
          if (clips.size === 0) {
            fatal =
              `no clips loaded from the authored pack (${AUTHORED_CLIP_STEMS.length} expected). ` +
              `Every cast member would stand in bind pose, which is worse than the shared master.`;
            ready = true;
            console.error('[cast]', fatal);
            return;
          }

          const ids = castIdsForMatch(assignment!);
          const res = await preloadCast(ids, levels, {
            onProgress: (done, total, id) => {
              if (done === total || done % 8 === 0) {
                console.info(`[cast] ${done}/${total} tiers loaded (…${id})`);
              }
            },
          });
          if (res.failed.length) {
            console.error(
              `[cast] ${res.failed.length} tier(s) REFUSED:\n` +
                res.failed.map((f) => `  ${f.id} LOD${f.lod}: ${f.why}`).join('\n'),
            );
          }
          if (res.loaded.length === 0) {
            fatal = `every cast model was refused — ${res.failed[0]?.why ?? 'no reason recorded'}`;
          }
          buildStaff(ctx);
          ready = true;
          console.info(
            `[cast] ${CAST_MANIFEST_VERSION}: ${res.loaded.length} tiers over ` +
              `${ids.length} characters at LOD${levels.join('/')}, ` +
              `${clips.size} clips, ${assignment!.onPitch.filter((s) => s.castId).length}/22 slots filled`,
          );
        } catch (e) {
          fatal = e instanceof Error ? e.message : String(e);
          ready = true;
          console.error('[cast] REFUSING TO DRAW:', fatal);
        }
      })();
    },

    onPitchConfig(config): void {
      if (config?.squadSize && config.squadSize !== squadSize) {
        squadSize = config.squadSize;
        assignment = assignCast({ squadSize });
        bySlot = new Map(assignment.onPitch.map((s) => [s.playerId, s]));
      }
      pitch = {
        length: config?.pitchLength ?? 105,
        width: config?.pitchWidth ?? 68,
      };
    },

    update(state: RenderState, ctx: RenderContext): void {
      if (!enabled || !ready || !sceneRef) return;
      const t0 = performance.now();
      const dt = state.dt;
      ctx.camera.getWorldPosition(camPos);

      // Read the witnesses the PREVIOUS render left, then open a new frame. Done
      // before anything else so a slot that stops drawing shows up as a falling
      // `submitted` rather than as a number that never moves.
      let submitted = 0;
      for (const s of slots) if (s && s.drawnFrame === frameId) submitted++;
      perf.submitted = submitted;
      frameId++;

      perf.players = 0;
      perf.drawCalls = 0;
      perf.triangles = 0;
      perf.lodHistogram = [0, 0, 0, 0];
      perf.swapsThisFrame = 0;
      liveSlots.length = 0;
      let swapBudget = SWAPS_PER_FRAME;

      for (let i = 0; i < SLOTS; i++) {
        const live = state.active[i] === 1;
        const s = slots[i];
        if (!live) {
          if (s?.active && s.visible) {
            s.active.inst.root.visible = false;
            s.visible = false;
            s.active.director.reset();
            if (s.casting) {
              for (const m of s.active.inst.meshes) m.castShadow = false;
              s.casting = false;
            }
          }
          mtx.makeTranslation(0, -1000, 0);
          blobs.setMatrixAt(i, mtx);
          continue;
        }

        let slot = s;
        if (!slot) {
          const assigned = bySlot.get(i);
          if (!assigned?.castId) continue; // the master path draws him; banner says why
          slot = {
            castId: assigned.castId,
            tiers: new Map(),
            lod: levels[0]!,
            active: null,
            accum: 0, band: 0, visible: false, casting: false, drawnFrame: -1,
            headLook: createHeadLookState(), headBone: null, headBoneTier: -1,
          };
          slots[i] = slot;
        }

        const px = state.px[i] as number;
        const py = state.py[i] as number;
        const pz = state.pz[i] as number;
        const yaw = state.yaw[i] as number;
        const d2 = (px - camPos.x) ** 2 + (py - camPos.y) ** 2 + (pz - camPos.z) ** 2;
        const dist = Math.sqrt(d2);
        camDist2[i] = d2;

        // ── LOD ─────────────────────────────────────────────────────────────
        const want = pinned >= 0 ? pinned : nearestLoaded(slot.castId, castLodStep(dist, slot.lod));
        // NEVER SWAP TIERS OUT FROM UNDER A ONE-SHOT.
        //
        // Every LOD tier owns its OWN PlayerAnimator + AnimationDirector
        // (`makeTier`), so a swap while a pass/shot/tackle is mid-flight used to
        // call `tier.director.reset()` on the INCOMING tier — clearing its
        // `last`/`lastExec` to null. The very next `drive()` call then reads
        // `state.anim` fresh, sees an edge (fresh director, nothing seen yet),
        // and calls `requestAction(cmd, {interrupt:true})` on the NEW tier's
        // animator — restarting the swing from frame 0 on the spot, however far
        // through it the old tier's animator had actually gotten. The old
        // comment here believed this "re-derived" the action; it re-derived the
        // STATE but discarded the PROGRESS, which is a visible pop back to the
        // wind-up mid-pass.
        //
        // Deferring the swap while `actionBusy` is the same ration this file
        // already applies to a denied upgrade: the slot keeps its current body
        // for another frame, which is invisible, and tries again next frame.
        // Actions are brief (well under a second) so the tier stays briefly
        // coarser or finer than distance would pick — cheap, and unlike a
        // restarted swing, unnoticeable.
        const busy = !!slot.active?.animator.actionBusy;
        if ((want !== slot.lod || !slot.active) && !busy) {
          const next = slot.tiers.get(want as CastLodLevel);
          // THE RATION GOVERNS UPGRADES, NEVER A FIRST APPEARANCE.
          //
          // A slot that already has a body and is denied a swap keeps that body
          // for another frame, which is invisible — that is what the ration was
          // written for. A slot with NO active tier that is denied draws
          // NOTHING, and twenty-two of those at two per frame is eleven frames
          // of empty pitch at every kickoff and every substitution. That is the
          // same failure this file was just repaired for, only briefer, and it
          // is the reason any screenshot taken in the first ~180 ms of a match
          // photographs grass. First tiers are always built.
          const first = !slot.active;
          const canSwap = next !== undefined || first || swapBudget > 0;
          if (canSwap) {
            const tier = next ?? tierFor(slot, want as CastLodLevel);
            if (tier) {
              if (!next) swapBudget--;
              if (slot.active && slot.active !== tier) {
                slot.active.inst.root.visible = false;
                if (slot.casting) {
                  for (const m of slot.active.inst.meshes) m.castShadow = false;
                }
                // Reachable now only between one-shots — the base gait, so
                // resetting the incoming tier's director is safe: there is no
                // in-flight action for it to discard.
                tier.director.reset();
              }
              slot.active = tier;
              slot.lod = want as CastLodLevel;
              slot.casting = false;
              perf.swapsThisFrame++;
            }
          }
        }
        const act = slot.active;
        if (!act) continue;

        // ── VISIBILITY BELONGS TO THE TIER, NOT TO THE SLOT ─────────────────
        //
        // THE BUG THIS REPLACES, because it cost half the cast and every
        // counter in this file called it healthy:
        //
        //     if (!slot.visible) { act.inst.root.visible = true;
        //                          slot.visible = true; }
        //
        // `makeTier` builds every tier with `root.visible = false`, and a swap
        // points `slot.active` at a root that has never been shown. The guard
        // above is a SLOT-level latch: it fires once, for the first tier a slot
        // ever draws, and is `true` forever after. So every LOD swap hid the
        // outgoing root, adopted an incoming root that was still hidden, and
        // left the latch closed — retiring that player permanently while
        // `perf.players` went on counting him.
        //
        // It is monotonic, which is what made it read as a culling bug: each
        // camera move re-bands more slots and each re-band loses another body.
        // Measured on a real practice match, quality `ultra`: `perf.players` 22
        // and 8 of 22 bodies submitted on a broadcast wide; then 2 of 22 on the
        // next camera pose, from the same run seconds later.
        //
        // Written from the ACTIVE ROOT every frame. Setting a boolean that is
        // already `true` costs nothing, and there is no latch left to desync
        // from the thing it describes. `slot.visible` survives only as a
        // question `slotPosition()` and the go-inactive path ask; it is no
        // longer permission to draw.
        act.inst.root.visible = true;
        slot.visible = true;

        // ⚠ `state.velX/velZ`, NOT `(px - prevPx) / dt`. The sim ticks at 60 Hz
        // and this loop runs at 150-180, so two frames in three are handed the
        // same position twice and that difference is exactly zero — which read
        // as Idle, while the third frame read 2-3x too fast and picked Run. The
        // owner filmed the result: a standing pose and a mid-stride pose
        // alternating every two frames at 30 fps. See `RenderState.velX`.
        const vx = state.velX[i] as number;
        const vz = state.velZ[i] as number;
        const speed = Math.hypot(vx, vz);
        const fx = Math.cos(yaw);
        const fz = Math.sin(yaw);
        const along = vx * fx + vz * fz;
        const lateral = vx * -fz + vz * fx;

        act.director.drive(state.anim[i] as number, state.animExec[i] as number, {
          speed, reversing: along < -0.15, lateral, dribbling: state.ball.possessedBy === i,
        });

        slot.band = bandForDistanceSq(d2);
        const hz = mixerHzForBand(slot.band);
        slot.accum += dt;
        if (slot.accum >= 1 / hz) {
          act.director.advance(slot.accum);
          slot.accum = 0;
        }

        // ── HEAD LOOK-AT (headLookAt.ts) ────────────────────────────────────
        // AFTER the mixer has posed the skeleton and BEFORE the world matrices
        // below are built, so the turn composes onto the clip's own head pose.
        // The bone is cached per TIER, because an LOD swap hands us a different
        // skeleton and a stale Object3D would rotate a body that is not drawn.
        if (slot.headBoneTier !== slot.lod) {
          slot.headBone = act.inst.root.getObjectByName('head') ?? null;
          slot.headBoneTier = slot.lod;
        }
        if (slot.headBone) {
          slot.headBone.getWorldPosition(headWorld);
          applyHeadLookAt(
            slot.headBone, slot.headLook, yaw, headWorld,
            state.ball.px, state.ball.py, state.ball.pz, speed, dt,
          );
          slot.headBone.updateMatrixWorld(true);
        }

        yawQ.setFromAxisAngle(up, Math.PI / 2 - yaw);
        // NO VERTICAL CORRECTION HERE ANY MORE. The root sits exactly where the
        // sim put him; what makes his boots meet the pitch is the leg-length
        // scale the animator applies to the hips channel (hipsLegScale.ts). The
        // constant lift that used to live on this line was right at rest and
        // wrong by (k-1)x the clip's own hip travel everywhere else, and adding
        // both would double-count the rest case.
        act.inst.root.position.set(px, py, pz);
        act.inst.root.quaternion.copy(yawQ);

        pos.set(px, py + 0.02, pz);
        mtx.compose(pos, idQ, scl);
        blobs.setMatrixAt(i, mtx);

        liveSlots.push(i);
        perf.players++;
        perf.drawCalls += act.inst.drawCalls;
        perf.triangles += act.inst.triangles;
        perf.lodHistogram[slot.lod]++;
      }

      // ── officials and coaches ────────────────────────────────────────────
      placeStaff(state, dt);

      if (shadowCasters > 0 && liveSlots.length > 0) {
        liveSlots.sort((a, z) => camDist2[a]! - camDist2[z]!);
      }
      for (let k = 0; k < liveSlots.length; k++) {
        const i = liveSlots[k]!;
        const slot = slots[i]!;
        const act = slot.active!;
        const wantCast = k < shadowCasters;
        if (wantCast !== slot.casting) {
          for (const m of act.inst.meshes) m.castShadow = wantCast;
          slot.casting = wantCast;
        }
        if (wantCast) {
          mtx.compose(pos.set(state.px[i] as number, -1000, state.pz[i] as number), idQ, blobHidden);
          blobs.setMatrixAt(i, mtx);
        }
      }
      blobs.instanceMatrix.needsUpdate = true;
      perf.animMs = performance.now() - t0;
      perf.textureBytesResident = residentTextureBytes();
    },

    setQuality(quality: QualitySettings): void {
      shadowCasters = SHADOW_CASTERS[quality.tier] ?? 0;
    },

    dispose(): void {
      for (let i = 0; i < SLOTS; i++) {
        const s = slots[i];
        if (!s) continue;
        for (const t of s.tiers.values()) {
          t.animator.dispose();
          t.inst.root.removeFromParent();
        }
        slots[i] = null;
      }
      for (const a of staff.values()) a.inst.root.removeFromParent();
      staff.clear();
      disposeCtx?.attachments.setHandProvider(null);
      disposeCtx?.attachments.setSilhouetteProvider(null);
      disposeCtx = null;
      blobs.removeFromParent();
      blobGeo.dispose();
      blobMat.dispose();
      sceneRef = null;
    },
  };

  // ── helpers that need the closure ─────────────────────────────────────────


  /**
   * The nearest tier that is actually resident.
   *
   * `castLevelsForQuality` may not have loaded the tier distance asks for (on
   * `medium` there is no LOD0 at all), and drawing nothing is not an option. The
   * search walks COARSER first: a player drawn one tier too cheap is a slightly
   * soft silhouette, a player drawn one tier too expensive on a machine that was
   * told not to is a frame-rate cliff.
   */
  function nearestLoaded(castId: string, want: CastLodLevel): CastLodLevel {
    if (castLodLoaded(castId, want)) return want;
    for (let l = want + 1; l <= 3; l++) {
      if (castLodLoaded(castId, l as CastLodLevel)) return l as CastLodLevel;
    }
    for (let l = want - 1; l >= 0; l--) {
      if (castLodLoaded(castId, l as CastLodLevel)) return l as CastLodLevel;
    }
    return want;
  }

  function residentTextureBytes(): number {
    // Counted off the manifest rather than off the GPU: `gl.info.memory.textures`
    // is a COUNT, and three exposes no byte total. The manifest's number is
    // RGBA8 + mips, which is what an uncompressed upload actually costs.
    let bytes = 0;
    for (const s of slots) {
      if (!s) continue;
      for (const lod of s.tiers.keys()) {
        const tier = castLodLoaded(s.castId, lod);
        if (tier) bytes += tierTextureBytes(s.castId, lod);
      }
    }
    return bytes;
  }

  function buildStaff(ctx: RenderContext): void {
    void ctx;
    for (const [key, ch] of [
      ['coach_home', castCoachFor('home')],
      ['coach_away', castCoachFor('away')],
      ['referee', castOfficial('referee')],
      ['assistant', castOfficial('assistant')],
    ] as const) {
      // ⚠ THESE TWO `continue`s USED TO BE SILENT, and a missing coach is
      // exactly the failure they hide. `buildStaff` runs ONCE, after the
      // preload, and is never retried — so a staff member whose tier did not
      // land is absent for the whole match with nothing written anywhere. The
      // owner reported "i cant see the coach" and there was no line in the
      // console to tell either of us which of the four had failed or why.
      // A staff member who cannot be drawn now says so by name.
      if (!ch) {
        console.warn(`[cast] staff "${key}": no character assigned — castRegistry `
          + 'returned nothing, so nobody will stand at this position.');
        continue;
      }
      const lod = nearestLoaded(ch.id, levels[levels.length - 1]!);
      const inst = instantiateCast(ch.id, lod);
      if (!inst) {
        console.warn(`[cast] staff "${key}" (${ch.id}): no tier loaded at LOD${lod} — `
          + `${castRefusals().get(ch.id) ?? 'not in the preload result'}. `
          + 'He is missing from the touchline for the whole match; buildStaff '
          + 'does not retry.');
        continue;
      }
      for (const m of inst.meshes) {
        m.castShadow = false;
        m.receiveShadow = true;
      }
      sceneRef?.add(inst.root);
      // ⚠ A TOUCHLINE IDLE, NOT A FOOTBALLER'S. Staff share the players' gait
      // ladder, and the ladder's base state is the clip literally named `Idle` —
      // which is a man waiting for a pass, weight forward, ready to move. On a
      // coach and on the referee that reads as a substitute about to come on.
      // `CoachIdle` is the same slot filled with a man standing and watching, so
      // swapping the entry is the whole change: no new state, no branch in the
      // animator, and every other gait still resolves normally.
      //
      // The swap is per-ACTOR, on a copied map. Mutating the shared `clips` here
      // would give twenty-two players the coach's stance, which is the same
      // class of mistake as the shared-clip mutation hipsLegScale.ts avoids.
      const staffClips = new Map(clips);
      const touchlineIdle = clips.get('CoachIdle');
      if (touchlineIdle) staffClips.set('Idle', touchlineIdle);
      const animator = new PlayerAnimator({
        clips: staffClips, root: inst.root, restHipsY: inst.restHipsY,
      });
      staff.set(key, {
        inst, animator, director: new AnimationDirector(animator), last: null,
        smoothSpeed: 0,
      });
    }

    // ⚠ THE SUBS WERE ASSIGNED, PRELOADED AND NEVER DRAWN. `assignCast` fills
    // `bench` with real characters and `castIdsForMatch` pulls their models
    // down with everyone else's — so the game has been paying for them all
    // along and putting nobody in the dugout. They go through the same actor
    // machinery as the staff, which is what gives them the gait ladder and the
    // leg-length scale for free.
    for (const [side, team] of [['home', 0], ['away', 1]] as const) {
      const subs = assignment?.bench[team] ?? [];
      subs.forEach((slot, i) => {
        if (!slot.castId) return;
        const lod = nearestLoaded(slot.castId, levels[levels.length - 1]!);
        const inst = instantiateCast(slot.castId, lod);
        if (!inst) {
          console.warn(`[cast] bench ${side}[${i}] (${slot.castId}): no tier at `
            + `LOD${lod} — he will be missing from the dugout.`);
          return;
        }
        for (const m of inst.meshes) {
          m.castShadow = false;
          m.receiveShadow = true;
        }
        sceneRef?.add(inst.root);
        const animator = new PlayerAnimator({
          clips, root: inst.root, restHipsY: inst.restHipsY,
        });
        staff.set(`bench_${side}_${i}`, {
          inst, animator, director: new AnimationDirector(animator), last: null,
          smoothSpeed: 0,
        });
      });
    }
  }

  /**
   * Coaches into the technical areas, officials onto the pitch — and now they
   * MOVE THEIR LEGS while they do it.
   *
   * ⚠ WHAT WAS WRONG. The old note here said staff were statues because "the
   * cast has no coaching or refereeing clips, and there is no referee in the
   * simulation to animate against", and that an official sliding around in bind
   * pose would be worse than one standing still. The second half was correct and
   * the first half hid it: `refereePlacement` follows the ball, so the referee
   * was never standing still. He glided.
   *
   * The premise that failed was "no clips". Staff load the SAME pack the players
   * do — an idle, a walk, a jog, a run — and the gait ladder needs one number to
   * choose between them. There is no `RenderState` entry for a referee to read a
   * velocity from, so the number is differentiated from the position they are
   * placed at, which is the same position the renderer is about to draw.
   *
   * ⚠ dt CAN BE ZERO on the first frame after a rebuild and after any pause, and
   * dividing by it gives an infinite speed that picks Sprint. Guarded below.
   * `last` is also cleared on teardown with the instances themselves, so a
   * rebuilt referee does not inherit a stale position from the far touchline and
   * sprint back across the pitch on his first frame.
   */
  function placeStaff(state: RenderState, dt: number): void {
    staffClock += dt;
    // No `official` flag any more: with the jog floor gone (see
    // STAFF_SPEED_TAU_S), officials and coaches are animated by the same
    // honest rule — the speed their placement actually moves at.
    const place = (
      key: string, x: number, z: number, yaw: number, chases = false,
    ): void => {
      const act = staff.get(key);
      if (!act) return;
      const { inst } = act;

      // ⚠ THE PLACEMENT IS A TARGET, NOT A POSITION — and treating it as a
      // position is the other half of the teleport. `refereePlacement` is a
      // pure function of where the ball IS, so a 30 m pass moves his target 30 m
      // in one frame and `position.set` put him there. Smoothing the SPEED (as
      // this function already did) only changed which gait played while he
      // teleported; it could not slow the teleport down, because nothing was
      // integrating.
      //
      // He now RUNS to his spot. Position is integrated toward the target at a
      // capped speed, so a long pass makes him set off and arrive a second or
      // two later — which is what a referee looks like, and is also why he stops
      // reading as a cursor locked to the ball. The lag IS the realism.
      //
      // Coaches do not chase: their placement is a fixed anchor at the dugout,
      // so integrating it would only add latency to a value that never moves.
      let px = x;
      let pz = z;
      if (chases) {
        const cur = act.last ?? { x, z };
        const gapX = x - cur.x;
        const gapZ = z - cur.z;
        const gap = Math.hypot(gapX, gapZ);
        // Ease into the sprint rather than snapping to the cap: closing a 2 m
        // gap at 7 m/s is its own kind of twitch. Proportional below the ease
        // distance, capped above it.
        const want = Math.min(OFFICIAL_MAX_SPEED_MS,
          gap / Math.max(OFFICIAL_EASE_M, 1e-3) * OFFICIAL_MAX_SPEED_MS);
        const step = Math.min(gap, want * dt);
        if (gap > 1e-4) {
          px = cur.x + (gapX / gap) * step;
          pz = cur.z + (gapZ / gap) * step;
        } else {
          px = cur.x;
          pz = cur.z;
        }
      }
      inst.root.position.set(px, 0, pz);

      const dx = act.last ? px - act.last.x : 0;
      const dz = act.last ? pz - act.last.z : 0;
      const travelled = act.last && dt > 1e-4 ? Math.hypot(dx, dz) / dt : 0;
      act.last = { x: px, z: pz };

      // ⚠ HE MUST FACE THE WAY HE IS RUNNING, OR THE JOG PLAYS SIDEWAYS.
      // `refereePlacement` returns a yaw that points at the BALL, which is the
      // right thing for a man standing and watching and the wrong thing for one
      // covering ground: his path is the diagonal, and the ball is rarely along
      // it. A forward gait under a ball-facing body is the crab-walk the old
      // statue at least avoided. So the body follows the TRAVEL direction while
      // he is moving, and falls back to the ball only when he has stopped —
      // where there is no travel direction to have.
      // Smooth BEFORE any decision reads the speed: raw differentiation of a
      // placement that follows the ball jitters frame to frame, and both the
      // facing choice and the gait ladder would flicker on it.
      act.smoothSpeed += (travelled - act.smoothSpeed)
        * Math.min(1, dt / STAFF_SPEED_TAU_S);
      const facing = act.smoothSpeed > FACING_FROM_TRAVEL_MS
        ? Math.atan2(dz, dx)
        : yaw;
      yawQ.setFromAxisAngle(up, Math.PI / 2 - facing);
      inst.root.quaternion.copy(yawQ);
      // The smoothed placement speed IS the animation speed — no floor. The
      // referee walks when his spot drifts, jogs when it moves, runs when play
      // breaks, and STANDS when it stops (owner: "always running fix that").
      const speed = act.smoothSpeed;
      // The officials' vocabulary is the gait ladder and nothing else: they have
      // no ball, so no action can be selected and none is offered. Passing the
      // idle command means the ladder alone decides, exactly as it does for a
      // player who is not carrying.
      act.director.drive(0, 0, { speed, reversing: false, lateral: 0 });
      act.director.advance(dt);

      perf.drawCalls += inst.drawCalls;
      perf.triangles += inst.triangles;
    };
    const home = coachPlacement('home', pitch, staffClock);
    const away = coachPlacement('away', pitch, staffClock);
    place('coach_home', home.x, home.z, home.yaw);
    place('coach_away', away.x, away.z, away.yaw);

    const ref = refereePlacement(state.ball.px, state.ball.pz, pitch);
    place('referee', ref.x, ref.z, ref.yaw, true);
    const asst = assistantPlacement(state.px, state.active, state.team, 1, true, pitch);
    place('assistant', asst.x, asst.z, asst.yaw, true);

    // The subs, lining their own dugout behind the coach's pacing lane. Their
    // placement never moves, so the ladder holds them on Idle — which is what a
    // man watching from the touchline is doing.
    for (const [side, team] of [['home', 0], ['away', 1]] as const) {
      const subs = assignment?.bench[team] ?? [];
      subs.forEach((slot, i) => {
        if (!slot.castId) return;
        const b = benchPlacement(side, i, subs.length, pitch);
        place(`bench_${side}_${i}`, b.x, b.z, b.yaw);
      });
    }
  }
}

/**
 * Texture bytes for one (character, tier), off the frozen manifest.
 *
 * Counted from the manifest rather than from the GPU because three exposes
 * `gl.info.memory.textures` as a COUNT and no byte total. The manifest's figure
 * is RGBA8 + a full mip chain, which is what an uncompressed upload costs.
 */
const TIER_TEXTURE_BYTES: ReadonlyMap<string, number> = new Map(
  CAST_CHARACTERS.flatMap((c) =>
    c.lods.map((l) => [`${c.id}#${l.lod}`, l.textureVramBytes] as const),
  ),
);

function tierTextureBytes(id: string, lod: CastLodLevel): number {
  return TIER_TEXTURE_BYTES.get(`${id}#${lod}`) ?? 0;
}
